import React, { useState, useEffect } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  DocumentService,
  getKYCStatusColor,
  getKYCVerificationLevelLabel,
  formatDate,
} from '@/services/documentService'
import { UserDocumentStatus } from '@/types'
import { Button } from '@/components/ui/button'
import { Shield, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react'

interface KYCStatusProps {
  onStatusUpdate?: () => void
}

export const KYCStatus: React.FC<KYCStatusProps> = ({ onStatusUpdate }) => {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [kycStatus, setKycStatus] = useState<UserDocumentStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadKYCStatus()
  }, [])

  const loadKYCStatus = async () => {
    try {
      setLoading(true)
      const data = await DocumentService.getUserKYCStatus()
      setKycStatus(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load KYC status')
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    loadKYCStatus()
    onStatusUpdate?.()
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: `${branding.primaryColor} transparent transparent transparent` }}></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="p-6 mb-8 transition-all duration-300 border card backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                <XCircle className="w-6 h-6" style={{ color: statusColors.error.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold" style={pageStyles.text}>Error loading KYC status</h3>
                <p className="mb-4 text-sm" style={{ color: statusColors.error.color }}>{error}</p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadKYCStatus}
                    className="hover:opacity-80"
                    style={{ backgroundColor: statusColors.error.backgroundColor, borderColor: statusColors.error.borderColor, color: branding.textColor }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="hover:opacity-80"
                    style={{ color: statusColors.error.color }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!kycStatus) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-8 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full mb-4" style={{ backgroundColor: `${branding.surfaceColor}30` }}>
                <Shield className="w-8 h-8" style={{ color: branding.textSecondaryColor }} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={pageStyles.text}>No KYC status available</h3>
              <p style={pageStyles.textSecondary}>Unable to load KYC verification status</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={pageStyles.container}>
      <div className="relative z-10 p-6">
        <div className="space-y-6">
          {/* KYC Status Overview */}
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold" style={pageStyles.text}>KYC Verification Status</h3>
                <Button
                  onClick={handleRefresh}
                  size="sm"
                  variant="outline"
                  className="hover:opacity-80"
                  style={{ backgroundColor: `${branding.primaryColor}20`, borderColor: `${branding.primaryColor}40`, color: branding.primaryColor }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${branding.primaryColor}10`, borderColor: `${branding.primaryColor}20` }}>
                  <div className="text-2xl font-bold" style={{ color: branding.primaryColor }}>
                    {kycStatus.kyc_completion_percentage}%
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Completion</div>
                </div>

                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${branding.surfaceColor}40`, borderColor: `${branding.textSecondaryColor}30` }}>
                  <div
                    className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${getKYCStatusColor(
                      kycStatus.kyc_status
                    )}`}
                  >
                    {kycStatus.kyc_status.replace('_', ' ')}
                  </div>
                  <div className="text-sm mt-1" style={pageStyles.textSecondary}>Status</div>
                </div>

                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${statusColors.info.backgroundColor}`, borderColor: `${statusColors.info.borderColor}` }}>
                  <div className="text-lg font-semibold" style={{ color: statusColors.info.color }}>
                    {kycStatus.documents.length}
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Documents</div>
                </div>

                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${statusColors.error.backgroundColor}`, borderColor: `${statusColors.error.borderColor}` }}>
                  <div className="text-lg font-semibold" style={{ color: statusColors.error.color }}>
                    {kycStatus.missing_required_documents.length}
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Missing</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1" style={pageStyles.textSecondary}>
                  <span>KYC Progress</span>
                  <span>{kycStatus.kyc_completion_percentage}%</span>
                </div>
                <div className="w-full rounded-full h-2" style={{ backgroundColor: `${branding.surfaceColor}40` }}>
                  <div
                    className="h-2 rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: branding.primaryColor,
                      width: `${kycStatus.kyc_completion_percentage}%`
                    }}
                  ></div>
                </div>
              </div>

              {/* Verification Details */}
              {kycStatus.is_kyc_verified && (
                <div className="border rounded-lg p-4" style={{ backgroundColor: statusColors.success.backgroundColor, borderColor: statusColors.success.borderColor }}>
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5" style={{ color: statusColors.success.color }} />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium" style={{ color: statusColors.success.color }}>
                        KYC Verification Complete
                      </h4>
                      <div className="mt-1 text-sm" style={{ color: statusColors.success.color }}>
                        <p>Verified on: {formatDate(kycStatus.kyc_verification_date!)}</p>
                        <p>
                          Level: {getKYCVerificationLevelLabel(kycStatus.kyc_verification_level)}
                        </p>
                        {kycStatus.kyc_verification_notes && (
                          <p>Notes: {kycStatus.kyc_verification_notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Missing Documents */}
              {kycStatus.missing_required_documents.length > 0 && (
                <div className="border rounded-lg p-4" style={{ backgroundColor: statusColors.warning.backgroundColor, borderColor: statusColors.warning.borderColor }}>
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5" style={{ color: statusColors.warning.color }} />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium" style={{ color: statusColors.warning.color }}>
                        Missing Required Documents
                      </h4>
                      <div className="mt-1 text-sm" style={{ color: statusColors.warning.color }}>
                        <p>Please upload the following documents to complete your KYC:</p>
                        <ul className="list-disc list-inside mt-1">
                          {kycStatus.missing_required_documents.map((docType, index) => (
                            <li key={index}>{docType}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Document Summary */}
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-6">
              <h4 className="text-lg font-semibold mb-4" style={pageStyles.text}>Document Summary</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${statusColors.warning.backgroundColor}`, borderColor: `${statusColors.warning.borderColor}` }}>
                  <div className="text-2xl font-bold" style={{ color: statusColors.warning.color }}>
                    {kycStatus.documents.filter(doc => doc.status === 'PENDING').length}
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Pending</div>
                </div>

                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${statusColors.success.backgroundColor}`, borderColor: `${statusColors.success.borderColor}` }}>
                  <div className="text-2xl font-bold" style={{ color: statusColors.success.color }}>
                    {kycStatus.documents.filter(doc => doc.status === 'APPROVED').length}
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Approved</div>
                </div>

                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${statusColors.error.backgroundColor}`, borderColor: `${statusColors.error.borderColor}` }}>
                  <div className="text-2xl font-bold" style={{ color: statusColors.error.color }}>
                    {kycStatus.documents.filter(doc => doc.status === 'REJECTED').length}
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Rejected</div>
                </div>

                <div className="text-center p-4 rounded-lg border" style={{ backgroundColor: `${branding.surfaceColor}40`, borderColor: `${branding.textSecondaryColor}30` }}>
                  <div className="text-2xl font-bold" style={{ color: branding.textSecondaryColor }}>
                    {kycStatus.documents.filter(doc => doc.status === 'EXPIRED').length}
                  </div>
                  <div className="text-sm" style={pageStyles.textSecondary}>Expired</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
